import Phaser from "phaser";
import { CANVAS_COLOURS, MAX_BRUSH_SIZE, frameId, type AssetManifest } from "daedalus-core";
import { pageTextureKey } from "./atlas.ts";

/**
 * One piece of a template ghost: a frame, its anchor in tiles and its flips.
 */
export interface GhostPiece {
  readonly tileset: string;
  readonly tile: number;
  readonly x: number;
  readonly y: number;
  readonly flipX: boolean;
  readonly flipY: boolean;
}

/**
 * A box in world pixels, a prop's art the brush's erase marks.
 */
export interface GhostBox {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * The most brush pieces drawn as images; past it each further piece is a dot at its foot point (ADR-123
 * decision 5.3).
 */
export const BRUSH_GHOSTS_MAX = 400;

/**
 * The radius of a brush ghost's dot, in world pixels.
 */
const BRUSH_DOT = 3;

/**
 * A translucent preview of what a click would place: the chosen prop frame anchored like `PropView` on
 * the cell under the pointer, an entity marker at the snapped position, the tile in hand on every cell
 * of the brush footprint (ADR-045) from a pool of `MAX_BRUSH_SIZE` squared images, or every piece of the
 * template in hand (ADR-066) from a pool that grows to the largest template shown. Hidden when nothing is
 * chosen or the pointer is off the map.
 */
export class PlacementGhost {
  private image: Phaser.GameObjects.Image | null = null;
  private readonly tiles: Phaser.GameObjects.Image[] = [];
  private readonly pieces: Phaser.GameObjects.Image[] = [];
  private readonly marker: Phaser.GameObjects.Graphics;
  private readonly ring: Phaser.GameObjects.Graphics;
  private frame: string | null = null;
  private made: ((object: Phaser.GameObjects.GameObject) => void) | null = null;

  /**
   * @param scene Scene to draw into.
   * @param manifest Asset manifest.
   * @param tileSize Tile size in pixels.
   * @param depth Depth above the map layers.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly manifest: AssetManifest,
    private readonly tileSize: number,
    private readonly depth: number,
  ) {
    this.marker = scene.add.graphics().setDepth(depth).setVisible(false);
    this.ring = scene.add.graphics().setDepth(depth).setVisible(false);
  }

  /**
   * How many tile ghost images are showing.
   *
   * @returns The count.
   */
  public get visibleTiles(): number {
    return this.tiles.filter((image) => image.visible).length;
  }

  /**
   * Shows a prop frame on a cell, or nothing for a frame the manifest lacks.
   *
   * @param tileset Frame tileset id.
   * @param tile Frame local tile.
   * @param cx Cell x.
   * @param cy Cell y.
   * @param flipX Mirror horizontally.
   * @param flipY Mirror vertically.
   */
  public showProp(tileset: string, tile: number, cx: number, cy: number, flipX = false, flipY = false): void {
    const id = frameId(tileset, null, tile);
    const rect = this.manifest.frames[id];
    if (rect === undefined) {
      this.hide();
      return;
    }
    const key = pageTextureKey(this.manifest, rect.page);
    if (this.image === null) {
      this.image = this.scene.add.image(0, 0, key, id).setOrigin(0, 1).setAlpha(0.6).setDepth(this.depth);
      this.made?.(this.image);
    } else if (this.frame !== id) {
      this.image.setTexture(key, id);
    }
    this.frame = id;
    this.image.setFlip(flipX, flipY).setPosition(cx * this.tileSize, (cy + 1) * this.tileSize).setVisible(true);
    this.marker.setVisible(false);
    this.ring.setVisible(false);
    this.hideTiles();
    this.hidePieces();
  }

  /**
   * Shows every piece of a template at its anchor, anchored like `PropView`, drawn in the order given.
   *
   * @param pieces The pieces, each at the anchor a placement would give it.
   */
  public showTemplate(pieces: readonly GhostPiece[]): void {
    this.ring.setVisible(false);
    this.drawPieces(pieces, pieces.length, 0.6);
    this.image?.setVisible(false);
    this.marker.setVisible(false);
    this.hideTiles();
  }

  /**
   * Shows the prop brush (ADR-123 decision 5): its circle as a ring, the pieces a press would place, or a running
   * stroke has placed, at the alpha given, the first `BRUSH_GHOSTS_MAX` as images and the rest as dots at their
   * foot points, and, while it erases, the ring in the erase colour with a box over each prop it would take.
   *
   * @param centreX The circle's centre in world pixels.
   * @param centreY The circle's centre in world pixels.
   * @param radius The radius in world pixels.
   * @param pieces The pieces, each at its anchor.
   * @param alpha The pieces' alpha.
   * @param erase Whether the brush erases.
   * @param marks The art boxes of the props an erase takes.
   * @param sizes The ring's line and a dot's radius in world pixels, for a camera zoomed out so far that the editor's own
   *   would be under a screen pixel (the overview, ADR-109 decision 46); the editor's own when omitted.
   */
  public showBrush(centreX: number, centreY: number, radius: number, pieces: readonly GhostPiece[], alpha: number, erase: boolean, marks: readonly GhostBox[], sizes?: { readonly line: number; readonly dot: number }): void {
    const shown = Math.min(pieces.length, BRUSH_GHOSTS_MAX);
    this.drawPieces(pieces, shown, alpha);
    const colour = erase ? CANVAS_COLOURS.brushErase : CANVAS_COLOURS.placementGhost;
    const g = this.ring;
    g.clear();
    g.lineStyle(sizes?.line ?? 2, colour.rgb, colour.alpha);
    g.strokeCircle(centreX, centreY, radius);
    g.fillStyle(CANVAS_COLOURS.placementGhost.rgb, alpha);
    for (let i = shown; i < pieces.length; i += 1) {
      const piece = pieces[i] as GhostPiece;
      const width = this.manifest.frames[frameId(piece.tileset, null, piece.tile)]?.w ?? 0;
      g.fillCircle(piece.x * this.tileSize + Math.floor(width / 2), (piece.y + 1) * this.tileSize, sizes?.dot ?? BRUSH_DOT);
    }
    if (marks.length > 0) {
      g.fillStyle(CANVAS_COLOURS.brushErase.rgb, 0.35);
      g.lineStyle(1, CANVAS_COLOURS.brushErase.rgb, 1);
      for (const box of marks) {
        g.fillRect(box.left, box.top, box.right - box.left, box.bottom - box.top);
        g.strokeRect(box.left, box.top, box.right - box.left, box.bottom - box.top);
      }
    }
    g.setVisible(true);
    this.image?.setVisible(false);
    this.marker.setVisible(false);
    this.hideTiles();
  }

  /**
   * Draws pieces from the pool, anchored like `PropView`, in the order given, hiding the rest of the pool. A piece
   * whose frame the manifest lacks is not drawn.
   *
   * @param pieces The pieces.
   * @param count How many of them to draw.
   * @param alpha Their alpha.
   */
  private drawPieces(pieces: readonly GhostPiece[], count: number, alpha: number): void {
    let used = 0;
    for (let index = 0; index < count; index += 1) {
      const piece = pieces[index] as GhostPiece;
      const id = frameId(piece.tileset, null, piece.tile);
      const rect = this.manifest.frames[id];
      if (rect === undefined) {
        continue;
      }
      const key = pageTextureKey(this.manifest, rect.page);
      let image = this.pieces[used];
      if (image === undefined) {
        image = this.scene.add.image(0, 0, key, id).setOrigin(0, 1).setAlpha(0.6).setDepth(this.depth);
        this.pieces.push(image);
        this.made?.(image);
      } else if (image.frame.name !== id) {
        image.setTexture(key, id);
      }
      image.setFlip(piece.flipX, piece.flipY).setAlpha(alpha).setPosition(piece.x * this.tileSize, (piece.y + 1) * this.tileSize).setVisible(true);
      used += 1;
    }
    for (let i = used; i < this.pieces.length; i += 1) {
      this.pieces[i]?.setVisible(false);
    }
  }

  /**
   * How many template ghost images are showing.
   *
   * @returns The count.
   */
  public get visiblePieces(): number {
    return this.pieces.filter((image) => image.visible).length;
  }

  /**
   * Hides the template pool.
   */
  private hidePieces(): void {
    for (const image of this.pieces) {
      image.setVisible(false);
    }
  }

  /**
   * Shows a tile frame on each cell of a brush footprint, mirrored as the tile in hand is (ADR-089), or nothing for a
   * frame the manifest lacks.
   *
   * @param tileset Frame tileset id.
   * @param tile Frame local tile.
   * @param cells Cell indices of the footprint, at most `MAX_BRUSH_SIZE` squared; extra cells are dropped.
   * @param mapWidth Map width in tiles, to place a cell index.
   * @param flipX Whether the tile is mirrored horizontally.
   * @param flipY Whether the tile is mirrored vertically.
   */
  public showTiles(tileset: string, tile: number, cells: Iterable<number>, mapWidth: number, flipX = false, flipY = false): void {
    const id = frameId(tileset, null, tile);
    const rect = this.manifest.frames[id];
    if (rect === undefined) {
      this.hide();
      return;
    }
    const key = pageTextureKey(this.manifest, rect.page);
    let used = 0;
    for (const index of cells) {
      if (used >= MAX_BRUSH_SIZE * MAX_BRUSH_SIZE) {
        break;
      }
      let image = this.tiles[used];
      if (image === undefined) {
        image = this.scene.add.image(0, 0, key, id).setOrigin(0, 0).setAlpha(0.6).setDepth(this.depth);
        this.tiles.push(image);
        this.made?.(image);
      } else if (image.frame.name !== id) {
        image.setTexture(key, id);
      }
      image.setFlip(flipX, flipY).setPosition((index % mapWidth) * this.tileSize, Math.floor(index / mapWidth) * this.tileSize).setVisible(true);
      used += 1;
    }
    for (let i = used; i < this.tiles.length; i += 1) {
      this.tiles[i]?.setVisible(false);
    }
    this.image?.setVisible(false);
    this.marker.setVisible(false);
    this.ring.setVisible(false);
    this.hidePieces();
  }

  /**
   * Hides the tile pool.
   */
  private hideTiles(): void {
    for (const image of this.tiles) {
      image.setVisible(false);
    }
  }

  /**
   * Shows an entity marker at a position.
   *
   * @param x Position x in tiles.
   * @param y Position y in tiles.
   */
  public showEntity(x: number, y: number): void {
    const g = this.marker;
    const r = this.tileSize * 0.35;
    const px = x * this.tileSize;
    const py = y * this.tileSize;
    g.clear();
    g.fillStyle(CANVAS_COLOURS.placementGhost.rgb, CANVAS_COLOURS.placementGhost.alpha * 0.5);
    g.fillTriangle(px, py - r, px + r, py, px - r, py);
    g.fillTriangle(px - r, py, px + r, py, px, py + r);
    g.setVisible(true);
    this.image?.setVisible(false);
    this.ring.setVisible(false);
    this.hideTiles();
    this.hidePieces();
  }

  /**
   * Names who is told of each object the ghost makes, once, when it is made, so a camera that has to be let to see
   * them (the overview's) is never handed a list again. The objects made so far are told at once.
   *
   * @param made Called with each object.
   */
  public onMade(made: (object: Phaser.GameObjects.GameObject) => void): void {
    this.made = made;
    for (const object of this.objects()) {
      made(object);
    }
  }

  /**
   * Every object the ghost has made so far. It allocates, so nothing calls it each frame.
   *
   * @returns The objects.
   */
  public objects(): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [this.marker, this.ring, ...this.tiles, ...this.pieces];
    if (this.image !== null) {
      out.push(this.image);
    }
    return out;
  }

  /**
   * Hides the ghost.
   */
  public hide(): void {
    this.image?.setVisible(false);
    this.marker.setVisible(false);
    this.ring.setVisible(false);
    this.hideTiles();
    this.hidePieces();
  }

  /**
   * Removes the ghost.
   */
  public destroy(): void {
    this.image?.destroy();
    this.marker.destroy();
    this.ring.destroy();
    for (const image of [...this.tiles, ...this.pieces]) {
      image.destroy();
    }
    this.tiles.length = 0;
    this.pieces.length = 0;
  }
}
